export type UserRevision = {
  id: string;
  data: Record<string, any>;
};

export type UserRevisionStore = {
  getUser: (uid: string) => Promise<Record<string, any> | undefined>;
  addRevision: (uid: string, data: Record<string, any>) => Promise<void>;
  replaceUser: (uid: string, data: Record<string, any>) => Promise<void>;
};

export type UserRevisionSummary = {
  id: string;
  email: string | null;
  name: string | null;
  subscriptionCount: number;
  timeLabel: string | null;
  voicePlanCount: number;
  newestVoicePlanMs: number | null;
  newestVoicePlanLabel: string | null;
};

/** Fields that belong to revision bookkeeping, not the live user document. */
const RESTORE_STRIP_KEYS = ['uid', 'revisions', 'createdAt'] as const;

export function prepareRevisionWrite(
  currentUserData: Record<string, any> | undefined,
  createdAt: unknown,
): Record<string, any> {
  return { ...(currentUserData ?? {}), createdAt };
}

export function prepareRestorePayload(revisionData: Record<string, any>): Record<string, any> {
  const payload = { ...revisionData };
  for (const key of RESTORE_STRIP_KEYS) {
    delete payload[key];
  }
  return payload;
}

function createdAtToMs(createdAt: unknown): number | null {
  if (createdAt == null) return null;
  if (typeof createdAt === 'number' && Number.isFinite(createdAt)) return createdAt;
  if (createdAt instanceof Date) {
    const ms = createdAt.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof createdAt === 'object') {
    const stamp = createdAt as {
      toMillis?: () => number;
      toDate?: () => Date;
      seconds?: number;
    };
    if (typeof stamp.toMillis === 'function') return stamp.toMillis();
    if (typeof stamp.toDate === 'function') {
      const ms = stamp.toDate().getTime();
      return Number.isNaN(ms) ? null : ms;
    }
    if (typeof stamp.seconds === 'number') return stamp.seconds * 1000;
  }
  if (typeof createdAt === 'string') {
    const parsed = Date.parse(createdAt);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function revisionTimeMs(revision: UserRevision): number | null {
  return createdAtToMs(revision.data?.createdAt);
}

export function voicePlanCountOf(data: Record<string, any> | undefined): number {
  return Array.isArray(data?.voicePlans) ? data.voicePlans.length : 0;
}

export function newestVoicePlanMsOf(data: Record<string, any> | undefined): number | null {
  const plans = data?.voicePlans;
  if (!Array.isArray(plans)) return null;
  let newest: number | null = null;
  for (const plan of plans) {
    const ms = createdAtToMs(plan?.timestamp);
    if (ms == null) continue;
    if (newest == null || ms > newest) newest = ms;
  }
  return newest;
}

function formatTimeLabel(ms: number | null): string | null {
  if (ms == null) return null;
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function sortRevisionsNewestFirst(revisions: UserRevision[]): UserRevision[] {
  return [...revisions].sort((a, b) => {
    const aCreated = revisionTimeMs(a);
    const bCreated = revisionTimeMs(b);
    if (aCreated != null && bCreated != null && aCreated !== bCreated) {
      return bCreated - aCreated;
    }

    const aPlan = newestVoicePlanMsOf(a.data);
    const bPlan = newestVoicePlanMsOf(b.data);
    if (aPlan != null && bPlan != null && aPlan !== bPlan) {
      return bPlan - aPlan;
    }
    if (aPlan != null && bPlan == null) return -1;
    if (aPlan == null && bPlan != null) return 1;

    return b.id.localeCompare(a.id);
  });
}

export function mapRevisionDocs(
  docs: Array<{ id: string; data: () => any }>,
): UserRevision[] {
  return docs.map((docSnap) => ({
    id: docSnap.id,
    data: docSnap.data() ?? {},
  }));
}

export function summarizeRevision(revision: UserRevision): UserRevisionSummary {
  const subscriptions = revision.data?.subscriptions;
  const name = typeof revision.data?.name === 'string' && revision.data.name.trim()
    ? revision.data.name
    : null;
  const email = typeof revision.data?.email === 'string' ? revision.data.email : null;
  const newestVoicePlanMs = newestVoicePlanMsOf(revision.data);
  return {
    id: revision.id,
    email,
    name,
    subscriptionCount: Array.isArray(subscriptions) ? subscriptions.length : 0,
    timeLabel: formatTimeLabel(revisionTimeMs(revision)),
    voicePlanCount: voicePlanCountOf(revision.data),
    newestVoicePlanMs,
    newestVoicePlanLabel: formatTimeLabel(newestVoicePlanMs),
  };
}

export async function restoreUserFromRevision(
  uid: string,
  revisionData: Record<string, any>,
  store: UserRevisionStore,
  createdAt: unknown,
): Promise<void> {
  const current = await store.getUser(uid);
  if (current === undefined) {
    throw new Error('User does not exist');
  }
  await store.addRevision(uid, prepareRevisionWrite(current, createdAt));
  await store.replaceUser(uid, prepareRestorePayload(revisionData));
}
